import * as tf from "@tensorflow/tfjs";

class ChatbotService {
  constructor() {
    this.model = null;
    this.vectorizer = null;
    this.idf = [];
    this.vocabulary = {};
    this.labelEncoder = null;
    this.contextEncoder = null;
    this.defaultContextVector = [];
    this.currentContext = [];
    this.tagResponses = {};
    this.tokenPattern = /\b\w+\b/g;
    this.maxFeatures = 100;
    this.chatHistory = [];
    this.contextConfidenceThreshold = 0.85;
  }

  async loadModel() {
    try {
      if (!this.model) {
        console.log("📦 Cargando modelo...");
        this.model = await tf.loadLayersModel("/modelo_vue/model.json");
        console.log("✅ Modelo cargado exitosamente");
      }
      await this.loadTokenizer();
    } catch (error) {
      console.error("❌ Error al cargar el modelo:", error);
    }
  }

  async loadTokenizer() {
    try {
      console.log("📦 Cargando datos del vectorizador y codificadores...");
      const response = await fetch("/model_keys.json");
      const data = await response.json();

      this.vocabulary = data.vectorizer.vocabulary;
      this.idf = data.vectorizer.idf;
      this.labelEncoder = data.label_encoder.classes;
      this.tagResponses = data.tag_responses;
      this.maxFeatures = data.vectorizer.max_features;
      this.defaultContextVector = data.context_encoder.without_context;
      this.currentContext = [...this.defaultContextVector];

      console.log("✅ Datos del vectorizador y codificadores cargados");
    } catch (error) {
      console.error("❌ Error al cargar datos del modelo:", error);
    }
  }

  extractTokens(text) {
    const tokens = (text.toLowerCase().match(this.tokenPattern) || []);
    console.log("🔍 Tokens extraídos:", tokens);
    return tokens;
  }

  extractNGrams(tokens) {
    const unigrams = tokens;
    const bigrams = tokens.slice(0, -1).map((t, i) => `${t} ${tokens[i + 1]}`);
    const ngrams = [...unigrams, ...bigrams];
    console.log("🔠 N-gramas generados:", ngrams);
    return ngrams;
  }

  vectorize(text) {
    try {
      const tokens = this.extractTokens(text);
      const ngrams = this.extractNGrams(tokens);
      const tfDict = {};

      ngrams.forEach(token => {
        if (Object.prototype.hasOwnProperty.call(this.vocabulary, token)) {
          tfDict[token] = (tfDict[token] || 0) + 1;
        }
      });

      const vector = new Array(this.maxFeatures).fill(0);
      for (const token in tfDict) {
        const index = this.vocabulary[token];
        if (index < this.maxFeatures) {
          const tfValue = tfDict[token];
          const idfValue = this.idf[index];
          vector[index] = tfValue * idfValue;
        }
      }

      console.log("🧮 Vector TF-IDF generado:", vector);
      return vector;
    } catch (error) {
      console.error("❌ Error al vectorizar el texto:", error);
      return new Array(this.maxFeatures).fill(0);
    }
  }

  async predict(userInput) {
    try {
      if (!this.model) {
        console.error("❌ Error: El modelo no está cargado.");
        return {
          response: "Error: Modelo no cargado.",
          hasContext: false
        };
      }

  
      console.log("📨 Entrada del usuario:", userInput);
  
      const vectorInput = this.vectorize(userInput);
      const inputTextTensor = tf.tensor2d([vectorInput]);
      const inputContextTensor = tf.tensor2d([this.currentContext]);
  
      console.log("🧠 Realizando predicciones...");
      const predictions = this.model.predict([inputTextTensor, inputContextTensor]);
      const [tagOutput, contextOutput] = await Promise.all([
        predictions[0].data(),
        predictions[1].data()
      ]);
  
      const tagIndex = tagOutput.indexOf(Math.max(...tagOutput));
      const tag = this.labelEncoder[tagIndex];
      const confidence = Math.max(...tagOutput);
  
      console.log(`🔖 Predicción de etiqueta: ${tag} (confianza: ${confidence.toFixed(3)})`);
  
      const contextConfidence = Math.max(...contextOutput);
      const contextIndex = contextOutput.indexOf(contextConfidence);
      const newContextVector = new Array(this.currentContext.length).fill(0)
      newContextVector[contextIndex] = 1;
      const hasContext = contextConfidence >= this.contextConfidenceThreshold  && !this.defaultContextVector.every((val, i) => val === newContextVector[i]);

  
      console.log(`🌐 Confianza del contexto: ${contextConfidence.toFixed(3)} (índice: ${contextIndex})`);
  
      if (hasContext) {
        
        
  
        if (!this.currentContext.every((val, i) => val === newContextVector[i])) {
          console.log("✅ Contexto actualizado");
        }
        this.currentContext = newContextVector;
      } else {
        this.currentContext = [...this.defaultContextVector];
        console.warn("⚠️ Confianza de contexto baja. Se ha restablecido el contexto por defecto.");
      }
  
      let response = "Lo siento, no entiendo tu pregunta o no tengo acceso a esa información.";
      if (confidence >= 0.85 && this.tagResponses[tag]) {
        response = this.tagResponses[tag][Math.floor(Math.random() * this.tagResponses[tag].length)];
      }
  
      this.chatHistory.push({ role: "assistant", content: response });
      console.log("💬 Respuesta generada:", response);
      console.log(hasContext)
  
      return {
        response,
        hasContext
      };
  
    } catch (error) {
      console.error("❌ Error durante la predicción:", error);
      return {
        response: "Hubo un error al procesar tu pregunta.",
        hasContext: false
      };
    }
  }
  
}

export default new ChatbotService();
